import { useState, useEffect } from 'react';
import { Trophy, TrendingUp, TrendingDown } from 'lucide-react';
import { apiService } from '../services/api';
import { Loading, ErrorMessage, Card } from '../components/SharedComponents';

function Standings() {
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStandings();
  }, []);

  const fetchStandings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getStandings();
      const data = response.data;

      let standingsData = [];
      if (data.standings && data.standings[0] && data.standings[0].standings) {
        standingsData = data.standings[0].standings;
      } else if (Array.isArray(data)) {
        standingsData = data;
      }

      setStandings(standingsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading message="Loading standings..." />;
  if (error) return <ErrorMessage message={error} onRetry={fetchStandings} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: '32px',
            fontWeight: '700',
            color: '#1c1917',
            letterSpacing: '-0.5px'
          }}>IPL Standings</h1>
          <p style={{ color: '#57534e', marginTop: '6px', fontSize: '15px' }}>League table and team positions</p>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 14px',
          backgroundColor: '#fafaf9',
          borderRadius: '10px',
          border: '1px solid #f5f5f4'
        }}>
          <Trophy style={{ width: '18px', height: '18px', color: '#e85d04' }} />
          <span style={{ fontWeight: '600', fontSize: '14px', color: '#1c1917' }}>{standings.length} Teams</span>
        </div>
      </div>

      <Card>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={tableHeaderStyle}>#</th>
                <th style={{ ...tableHeaderStyle, textAlign: 'left' }}>Team</th>
                <th style={tableHeaderStyle}>P</th>
                <th style={tableHeaderStyle}>W</th>
                <th style={tableHeaderStyle}>L</th>
                <th style={tableHeaderStyle}>NR</th>
                <th style={tableHeaderStyle}>Pts</th>
                <th style={tableHeaderStyle}>NRR</th>
                <th className="hide-mobile" style={tableHeaderStyle}>Last 5</th>
              </tr>
            </thead>
            <tbody>
              {standings.map((team, index) => {
                const netRR = parseFloat(team.netrr || 0);
                const lastFive = team.lastfivematchresult?.split(',') || [];
                const isQualified = index < 4;

                return (
                  <tr
                    key={team.team_id || index}
                    style={{
                      transition: 'background-color 0.15s',
                      backgroundColor: isQualified ? 'rgba(34, 197, 94, 0.04)' : 'transparent'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isQualified ? 'rgba(34, 197, 94, 0.08)' : '#fafaf9'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isQualified ? 'rgba(34, 197, 94, 0.04)' : 'transparent'}
                  >
                    <td style={tableCellStyle}>
                      <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '8px',
                        backgroundColor: isQualified ? '#dcfce7' : '#f5f5f4',
                        color: isQualified ? '#22c55e' : '#57534e',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '600',
                        fontSize: '13px',
                        margin: '0 auto'
                      }}>
                        {index + 1}
                      </div>
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: 'left' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {team.team?.logo_url && (
                          <img
                            src={team.team.logo_url}
                            alt={team.team.title}
                            style={{ width: '32px', height: '32px', objectFit: 'contain', borderRadius: '6px' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <div>
                          <p style={{ fontWeight: '600', color: '#1c1917', margin: 0, fontSize: '14px' }}>
                            {team.team?.title || team.team?.name || 'N/A'}
                          </p>
                          <p style={{ fontSize: '11px', color: '#a8a29e', margin: 0, fontWeight: '500' }}>
                            {team.team?.abbr || ''}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td style={tableCellStyle}>{team.played || 0}</td>
                    <td style={{ ...tableCellStyle, color: '#22c55e', fontWeight: '600' }}>{team.win || 0}</td>
                    <td style={{ ...tableCellStyle, color: '#ef4444', fontWeight: '600' }}>{team.loss || 0}</td>
                    <td style={{ ...tableCellStyle, color: '#57534e' }}>{team.nr || 0}</td>
                    <td style={{ ...tableCellStyle, fontWeight: '700', color: '#e85d04' }}>{team.points || 0}</td>
                    <td style={tableCellStyle}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        color: netRR >= 0 ? '#22c55e' : '#ef4444',
                        fontWeight: '500'
                      }}>
                        {netRR >= 0 ? (
                          <TrendingUp style={{ width: '14px', height: '14px' }} />
                        ) : (
                          <TrendingDown style={{ width: '14px', height: '14px' }} />
                        )}
                        {netRR >= 0 ? '+' : ''}{netRR.toFixed(3)}
                      </div>
                    </td>
                    <td className="hide-mobile" style={tableCellStyle}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        {lastFive.map((result, i) => (
                          <div
                            key={i}
                            style={{
                              width: '22px',
                              height: '22px',
                              borderRadius: '6px',
                              backgroundColor: result === 'W' ? '#22c55e' : result === 'L' ? '#ef4444' : '#a8a29e',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '10px',
                              fontWeight: '700'
                            }}
                          >
                            {result}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

const tableHeaderStyle = {
  padding: '12px 14px',
  textAlign: 'center',
  fontWeight: '600',
  fontSize: '12px',
  color: '#57534e',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  borderBottom: '1px solid #e7e5e4',
  backgroundColor: '#fafaf9'
};

const tableCellStyle = {
  padding: '14px',
  textAlign: 'center',
  fontSize: '14px',
  color: '#1c1917',
  borderBottom: '1px solid #f5f5f4'
};

export default Standings;
